import { Button, DatePicker, Space, Spin, Table, Tag, Typography } from "antd";
import { FaFileExcel, FaFileCsv } from "react-icons/fa";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useAppAuth } from "../../../context/use-app-auth";
import { useTranslation } from "react-i18next";
import * as XLSX from "sheetjs-style";
import FileSaver from "file-saver";
import requets from "../../../api";
import { useColumnsTable } from "./columns.table";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const EXCEL_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";

const RANGE_PRESETS = [
  {
    label: "Hoy",
    value: [dayjs().startOf("day"), dayjs().endOf("day")],
  },
  {
    label: "Ayer",
    value: [
      dayjs().subtract(1, "day").startOf("day"),
      dayjs().subtract(1, "day").endOf("day"),
    ],
  },
  {
    label: "Últimos 7 días",
    value: [dayjs().subtract(6, "day").startOf("day"), dayjs().endOf("day")],
  },
  {
    label: "Últimos 30 días",
    value: [dayjs().subtract(29, "day").startOf("day"), dayjs().endOf("day")],
  },
  {
    label: "Este mes",
    value: [dayjs().startOf("month"), dayjs().endOf("month")],
  },
];

// History response: { fields: [{ field, unit, data: [{value, timestamp}] }] }
// → Pivot to: [{ timestamp, [field1]: v1, [field2]: v2, ... }]
function pivotFromFields(fields) {
  if (!Array.isArray(fields) || fields.length === 0) return [];
  const rowMap = {};
  fields.forEach(({ field, data }) => {
    (data || []).forEach(({ value, timestamp }) => {
      if (!rowMap[timestamp]) rowMap[timestamp] = { timestamp };
      rowMap[timestamp][field] = value;
    });
  });
  return Object.values(rowMap).sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp),
  );
}

function doExcelExport(rows, columns, fileName) {
  const exportData = rows.map((row) => {
    const out = {};
    columns.forEach((col) => {
      out[col.title] = row[col.dataIndex] ?? "";
    });
    return out;
  });

  const ws = XLSX.utils.json_to_sheet(exportData);
  for (const addr in ws) {
    if (addr.startsWith("!")) continue;
    ws[addr].s = {
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      },
    };
    if (/^[A-Z]+1$/.test(addr)) {
      ws[addr].s.fill = { fgColor: { rgb: "1D4ED8" } };
      ws[addr].s.font = { bold: true, color: { rgb: "FFFFFF" } };
    }
  }

  const wb = { Sheets: { Reporte: ws }, SheetNames: ["Reporte"] };
  const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  FileSaver.saveAs(new Blob([buffer], { type: EXCEL_MIME }), `${fileName}.xlsx`);
}

function doCsvExport(rows, columns, fileName) {
  const headers = columns.map((c) => `"${c.title}"`).join(",");
  const body = rows.map((row) =>
    columns
      .map((c) => {
        const val = row[c.dataIndex] ?? "";
        return typeof val === "string"
          ? `"${val.replace(/"/g, '""')}"`
          : val;
      })
      .join(","),
  );
  const csv = "﻿" + [headers, ...body].join("\n");
  FileSaver.saveAs(
    new Blob([csv], { type: "text/csv;charset=utf-8;" }),
    `${fileName}.csv`,
  );
}

export const TableReport = ({ t, device }) => {
  const auth = useAppAuth();
  const req = useMemo(
    () => requets(auth.user?.access_token),
    [auth.user?.access_token],
  );
  const { t: tFallback } = useTranslation("global");
  const translate = t || tFallback;

  const defaultRange = [
    dayjs().subtract(6, "day").startOf("day"),
    dayjs().endOf("day"),
  ];

  const [dateRange, setDateRange] = useState(defaultRange);
  const [rawFields, setRawFields] = useState([]);
  const [spinning, setSpinning] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const columns = useColumnsTable(device);
  const pivotedData = useMemo(() => pivotFromFields(rawFields), [rawFields]);

  const measurementCount = Math.max(0, columns.length - 1); // exclude timestamp column

  const fileName = `reporte_${(device?.name ?? "device").replace(/\s+/g, "_")}_${dayjs().format("YYYY-MM-DD")}`;

  useEffect(() => {
    if (!device?.devEui) return;
    setCurrentPage(1);

    const run = async () => {
      setSpinning(true);
      try {
        const start = dateRange[0].toISOString();
        const stop = dateRange[1].toISOString();
        const response = await req.get(`/metrics/${device.devEui}/history`, {
          start,
          stop,
        });
        setRawFields(response.data?.fields ?? []);
      } catch {
        setRawFields([]);
      } finally {
        setSpinning(false);
      }
    };

    run();
  }, [device?.devEui, dateRange, req]);

  return (
    <Spin spinning={spinning} tip={translate("loading")}>
      {/* Device info + download row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 14,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {device?.name && (
            <>
              <Text strong style={{ fontSize: 14 }}>
                {device.name}
              </Text>
              <Tag
                color={device.isActive ? "success" : "default"}
                style={{ margin: 0, fontSize: 11 }}
              >
                {device.isActive
                  ? translate("reports.tableColumn.stateValueOn")
                  : translate("reports.tableColumn.stateValueOff")}
              </Tag>
              <Text
                type="secondary"
                style={{ fontSize: 11, fontFamily: "monospace" }}
              >
                {device.devEui}
              </Text>
            </>
          )}
        </div>

        <Space size={6}>
          <Button
            size="small"
            icon={<FaFileExcel style={{ fontSize: 12 }} />}
            onClick={() => doExcelExport(pivotedData, columns, fileName)}
            disabled={pivotedData.length === 0}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              color: "#15803d",
              borderColor: "#86efac",
            }}
          >
            {translate("reports.excel")}
          </Button>
          <Button
            size="small"
            icon={<FaFileCsv style={{ fontSize: 12 }} />}
            onClick={() => doCsvExport(pivotedData, columns, fileName)}
            disabled={pivotedData.length === 0}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              color: "#15803d",
              borderColor: "#86efac",
            }}
          >
            {translate("reports.csv")}
          </Button>
        </Space>
      </div>

      {/* Toolbar: date range */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
          background: "#FAFAF9",
          borderRadius: 8,
          border: "1px solid rgba(0,0,0,0.07)",
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <RangePicker
          format="DD/MM/YYYY"
          value={dateRange}
          onChange={(dates) => {
            if (dates) setDateRange(dates);
          }}
          presets={RANGE_PRESETS}
          allowClear={false}
          size="small"
        />

        <div
          style={{
            marginLeft: "auto",
            fontSize: 12,
            color: "#9CA3AF",
            flexShrink: 0,
          }}
        >
          {measurementCount} {translate("reports.variables")} ·{" "}
          {pivotedData.length} {translate("reports.records")}
        </div>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={pivotedData}
        scroll={{ x: "max-content" }}
        rowKey="timestamp"
        size="small"
        locale={{ emptyText: "Sin datos para el rango seleccionado" }}
        pagination={{
          current: currentPage,
          pageSize,
          total: pivotedData.length,
          showTotal: (total) =>
            `Total ${total} ${translate("reports.records")}`,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100", "200"],
          position: ["bottomRight"],
          size: "small",
        }}
        style={{
          border: "1px solid rgba(0,0,0,0.08)",
          borderRadius: 8,
          overflow: "hidden",
        }}
      />
    </Spin>
  );
};
